/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { saveAs } from "file-saver";
import { UserPlus, UserMinus, UserX, UserCog, Download, BarChart3, Search, Loader2 } from "lucide-react";
import axiosInstance from "../services/Axios/axios";
import PageSkeleton from "./PageSkeleton";

const FUND_TABS = [
  { key: "newContributors", label: "New Contributors", icon: <UserPlus size={15} />, accent: "emerald", hint: "Contributed this period for the very first time" },
  { key: "nonContributors", label: "Non-Contributors", icon: <UserMinus size={15} />, accent: "amber", hint: "Contributed the previous period but missing this period" },
  { key: "stopped", label: "Stopped", icon: <UserX size={15} />, accent: "rose", hint: "Absent for the last 6 consecutive periods" },
  { key: "unregistered", label: "Not Registered", icon: <UserCog size={15} />, accent: "violet", hint: "Contributed this period but has no member account yet" },
];

const LOAN_TABS = [
  { key: "newContributors", label: "New Loans", icon: <UserPlus size={15} />, accent: "emerald", hint: "First loan deduction appeared this period" },
  { key: "nonContributors", label: "Missed This Period", icon: <UserMinus size={15} />, accent: "amber", hint: "Deducted the previous period but missing this one (may include completed loans)" },
  { key: "stopped", label: "Long Inactive", icon: <UserX size={15} />, accent: "rose", hint: "No loan deduction in the last 6 periods (often completed loans)" },
  { key: "unregistered", label: "Not Registered", icon: <UserCog size={15} />, accent: "violet", hint: "Loan deduction this period but no member account yet" },
];

const FUND_INTRO =
  "After uploading fund deductions, pick the period to see who is new, who stopped, who didn't contribute, and who is contributing without a member account — then export the list.";
const LOAN_INTRO =
  "After uploading loan deductions, pick the period to see whose loan deduction didn't appear this period (vs last), plus new and long-inactive loans — then export the list.";

const VARIANTS = {
  funds: { periodsEndpoint: "/funds/periods", analysisEndpoint: "/funds/period-analysis", intro: FUND_INTRO, tabs: FUND_TABS, showLoanStatus: false },
  loans: { periodsEndpoint: "/loans/periods", analysisEndpoint: "/loans/period-analysis", intro: LOAN_INTRO, tabs: LOAN_TABS, showLoanStatus: true },
};

// Best-effort loan application status (from ApplyLoan) — many deductions have
// no application record, so "unknown" is common and expected.
function LoanStatusBadge({ status }) {
  const s = (status || "unknown").toLowerCase();
  const styles = {
    paid: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    initiated: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    booked: "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
    submitted: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    rejected: "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    unknown: "bg-slate-100 text-slate-500 dark:bg-gray-700 dark:text-slate-400",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${styles[s] || styles.unknown}`}>
      {s}
    </span>
  );
}

const accentClasses = {
  emerald: { card: "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800", icon: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400", num: "text-emerald-700 dark:text-emerald-300", tab: "bg-emerald-600 text-white" },
  amber:   { card: "bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800", icon: "bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400", num: "text-amber-700 dark:text-amber-300", tab: "bg-amber-600 text-white" },
  rose:    { card: "bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-800", icon: "bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400", num: "text-rose-700 dark:text-rose-300", tab: "bg-rose-600 text-white" },
  violet:  { card: "bg-violet-50 dark:bg-violet-900/20 border-violet-100 dark:border-violet-800", icon: "bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400", num: "text-violet-700 dark:text-violet-300", tab: "bg-violet-600 text-white" },
};

/**
 * Self-contained deduction period-analysis UI (no page layout). Reused for
 * both funds and loans via the `variant` prop. Mounted inside both the
 * IT Officer and Admin layouts.
 *
 * Props:
 *  accent  - button / focus-ring color key ("cyan" | "indigo").
 *  variant - "funds" (default) or "loans": selects endpoints, labels, and
 *            whether the Loan Status column is shown.
 */
function FundContributionAnalysisPanel({ accent = "cyan", variant = "funds" }) {
  const { periodsEndpoint, analysisEndpoint, intro, tabs, showLoanStatus } =
    VARIANTS[variant] || VARIANTS.funds;
  const TABS = tabs;
  const ring = accent === "indigo" ? "focus:ring-indigo-500" : "focus:ring-cyan-500";
  const btn = accent === "indigo" ? "bg-indigo-600 hover:bg-indigo-700" : "bg-cyan-600 hover:bg-cyan-700";
  const ringActive = accent === "indigo" ? "ring-indigo-500" : "ring-cyan-500";
  const exportBtn = accent === "indigo"
    ? "text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
    : "text-cyan-700 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 hover:bg-cyan-100 dark:hover:bg-cyan-900/40";

  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState("");
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("newContributors");
  const [rowSearch, setRowSearch] = useState("");

  // Load the list of available periods (ordered chronologically by the backend).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get(periodsEndpoint);
        const list = Array.isArray(data) ? data : [];
        setPeriods(list);
        if (list.length) setPeriod(list[list.length - 1].period); // default to latest
      } catch (e) {
        toast.error("Could not load periods");
      } finally {
        setLoadingPeriods(false);
      }
    })();
  }, []);

  const runAnalysis = async () => {
    if (!period) return toast.info("Select a period first");
    setRunning(true);
    setResult(null);
    try {
      const { data } = await axiosInstance.get(analysisEndpoint, { params: { period } });
      setResult(data);
      setActiveTab("newContributors");
      setRowSearch("");
    } catch (e) {
      const msg = e?.response?.data?.message ?? "Analysis failed. Try again.";
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  const activeRows = useMemo(() => {
    const rows = result?.[activeTab] ?? [];
    const q = rowSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.staff_id?.toLowerCase().includes(q) ||
        r.name?.toLowerCase().includes(q) ||
        r.facility?.toLowerCase().includes(q)
    );
  }, [result, activeTab, rowSearch]);

  const exportCsv = () => {
    const rows = result?.[activeTab] ?? [];
    if (!rows.length) return toast.info("Nothing to export");
    const headers = ["staff_id", "name", "facility", "phone_number", "region", "district"];
    if (showLoanStatus) headers.push("loan_status");
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
    ].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `${activeTab}_${result.period}.csv`);
    // The export happens entirely in the browser, so the API can only know
    // about it if we tell it. Fire-and-forget: a failed audit write must never
    // make a successful download look broken.
    axiosInstance
      .post("/audit/event", {
        action: "ANALYSIS_EXPORT",
        period: result.period,
        list: activeTab,
        variant,
        description: `Exported the ${
          TABS.find((t) => t.key === activeTab)?.label ?? activeTab
        } list for ${result.period} (${rows.length} rows)`,
      })
      .catch(() => {});
  };

  const summary = result?.summary;

  return (
    <>
      <p className="text-sm text-slate-500 dark:text-slate-400 -mt-2 mb-6">{intro}</p>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm p-5 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">Period</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              disabled={loadingPeriods}
              className={`w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-800 dark:text-slate-200 text-sm focus:ring-2 ${ring} focus:border-transparent outline-none transition-all disabled:opacity-50`}
            >
              {loadingPeriods && <option>Loading periods…</option>}
              {!loadingPeriods && !periods.length && <option value="">No periods found</option>}
              {periods
                .slice()
                .reverse()
                .map((p) => (
                  <option key={p.period} value={p.period}>
                    {p.period}
                  </option>
                ))}
            </select>
          </div>
          <button
            onClick={runAnalysis}
            disabled={running || !period}
            className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-semibold ${btn} transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {running ? <Loader2 size={16} className="animate-spin" /> : <BarChart3 size={16} />}
            {running ? "Analyzing…" : "Run Analysis"}
          </button>
        </div>
        {result && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
            Comparing <span className="font-semibold text-slate-700 dark:text-slate-200">{result.period}</span>
            {result.previousPeriod && (
              <> against previous period <span className="font-semibold text-slate-700 dark:text-slate-200">{result.previousPeriod}</span></>
            )}
            {" · "}{summary?.currentContributors?.toLocaleString()} contributors this period
          </p>
        )}
      </div>

      {/* Summary cards */}
      {result && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {TABS.map((t) => {
            const c = accentClasses[t.accent];
            const count = result[t.key]?.length ?? 0;
            return (
              <button
                key={t.key}
                onClick={() => { setActiveTab(t.key); setRowSearch(""); }}
                className={`text-left p-5 rounded-2xl border ${c.card} transition-all ${activeTab === t.key ? `ring-2 ring-offset-1 ${ringActive} dark:ring-offset-gray-900` : "hover:shadow-md"}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.icon}`}>{t.icon}</div>
                  <span className={`text-2xl font-black ${c.num}`}>{count.toLocaleString()}</span>
                </div>
                <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{t.label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t.hint}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Results table */}
      {result && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                {TABS.find((t) => t.key === activeTab)?.label}
              </h3>
              <span className="text-xs text-slate-400">({activeRows.length.toLocaleString()})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={rowSearch}
                  onChange={(e) => setRowSearch(e.target.value)}
                  placeholder="Filter staff ID, name, facility"
                  className={`pl-8 pr-3 py-2 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-800 dark:text-slate-200 text-xs focus:ring-2 ${ring} focus:border-transparent outline-none transition-all w-full sm:w-56`}
                />
              </div>
              <button
                onClick={exportCsv}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold ${exportBtn} transition-colors whitespace-nowrap`}
              >
                <Download size={14} /> Export CSV
              </button>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0">
                <tr className="bg-slate-50 dark:bg-gray-700/50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Staff ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Facility</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Region</th>
                  {showLoanStatus && <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Loan Status</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                {activeRows.length > 0 ? (
                  activeRows.map((r, i) => (
                    <tr key={r.staff_id + i} className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="px-4 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-300">{r.staff_id}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300">{r.name || <span className="text-slate-400 italic">No member record</span>}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400">{r.facility || "—"}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400">{r.phone_number || "—"}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400">{r.region || "—"}</td>
                      {showLoanStatus && (
                        <td className="px-4 py-2.5 text-sm">
                          <LoanStatusBadge status={r.loan_status} />
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={showLoanStatus ? 6 : 5} className="px-4 py-12 text-center text-sm text-slate-400 dark:text-slate-500">
                      No records in this list
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {running && <PageSkeleton cards={4} cols={5} />}

      {!result && !running && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-slate-200 dark:border-gray-700 p-12 text-center">
          <BarChart3 size={32} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Select a period and run the analysis to see results.</p>
        </div>
      )}

      <ToastContainer autoClose={3000} />
    </>
  );
}

export default FundContributionAnalysisPanel;
